import { useRef } from 'react';
import { useMutationMediaObjectLabelsUpdate } from '@src/queries/media/useMutationMediaObjectLabelsUpdate';
import { ApiTypes, MediaView, ProductView } from 'common';
import { ArrayHelper } from '@src/services/helpers/array';

interface ProductLinkState {
  mediaId: string;
  exact: string[];
  similar: string[];
}

export const useProductLinking = (
  media: MediaView,
) => {
  const mutation = useMutationMediaObjectLabelsUpdate();
  const { isPending: isLoading } = mutation;
  const linkStateRef = useRef<ProductLinkState | undefined>(undefined);
  const observedMediaStateRef = useRef<ProductLinkState | undefined>(undefined);
  const pendingLocalMutationsRef = useRef(0);
  const mutationQueueRef = useRef<Promise<void> | undefined>(undefined);

  const mediaState: ProductLinkState = {
    mediaId: media._id,
    exact: [...(media.productIdsExact ?? [])],
    similar: [...(media.productIdsSimilar ?? [])],
  };

  if (linkStateRef.current?.mediaId !== media._id) {
    linkStateRef.current = mediaState;
    pendingLocalMutationsRef.current = 0;
  } else if (
    pendingLocalMutationsRef.current === 0
    && !areProductLinkStatesEqual(observedMediaStateRef.current, mediaState)
  ) {
    // A refetch or another component may have changed the same media while
    // this hook was mounted. Adopt it when there is no local optimistic state
    // to preserve; pending local mutations remain the source of truth until
    // they settle.
    linkStateRef.current = mediaState;
  }
  observedMediaStateRef.current = mediaState;

  const updateProductIds = (
    existingIds: string[] | undefined,
    newIds: string[],
    shouldMerge: boolean
  ): string[] => {
    // If shouldMerge is true, we merge the newIds with the existingIds.
    // If shouldMerge is false, we remove the newIds from the existingIds.
    return shouldMerge
      ? ArrayHelper.uniqBy<string, string>([...(existingIds ?? []), ...newIds])
      : (existingIds ?? []).filter(id => !newIds.includes(id));
  };

  const mutateMedia = (
    updatedExactIds?: string[],
    updatedSimilarIds?: string[],
    previousState?: ProductLinkState,
  ) => {
    const params: ApiTypes.ParamsPutObjectLabels = {
      medias: [
        {
          mediaId: media._id,
          productIdsExact: updatedExactIds,
          productIdsSimilar: updatedSimilarIds,
        }
      ],
    };

    const optimisticState: ProductLinkState = {
      mediaId: media._id,
      exact: [...(updatedExactIds ?? [])],
      similar: [...(updatedSimilarIds ?? [])],
    };
    const rollbackState = previousState ?? {
      mediaId: media._id,
      exact: [...(media.productIdsExact ?? [])],
      similar: [...(media.productIdsSimilar ?? [])],
    };

    // Product linking is a full replacement update. Queue requests for this
    // detail view so a later click cannot race an earlier replacement.
    pendingLocalMutationsRef.current += 1;
    const previousMutation = mutationQueueRef.current;
    const nextMutation = previousMutation
      ? previousMutation.catch(() => undefined).then(() => mutation.mutateAsync(params))
      : mutation.mutateAsync(params);
    mutationQueueRef.current = nextMutation.catch(() => undefined);

    void nextMutation.then(
      () => {
        pendingLocalMutationsRef.current = Math.max(0, pendingLocalMutationsRef.current - 1);
      },
      () => {
        pendingLocalMutationsRef.current = Math.max(0, pendingLocalMutationsRef.current - 1);
      },
    );

    void nextMutation.catch(() => {
      // Keep a later optimistic click intact. If this was the latest update,
      // restore the state that was visible before this request.
      const currentState = linkStateRef.current;
      if (
        currentState?.mediaId === optimisticState.mediaId
        && areProductIdsEqual(currentState.exact, optimisticState.exact)
        && areProductIdsEqual(currentState.similar, optimisticState.similar)
      ) {
        linkStateRef.current = rollbackState;
      }
    });
  };

  const linkProducts = (products: ProductView[], linkType: string) => {
    const currentState = linkStateRef.current!;
    const previousState: ProductLinkState = {
      ...currentState,
      exact: [...currentState.exact],
      similar: [...currentState.similar],
    };
    const productIds = ArrayHelper.uniqBy(products.map((p) => p.id));
    const updatedExactIds = linkType === 'exact'
      ? updateProductIds(currentState.exact, productIds, true)
      : currentState.exact;

    const updatedSimilarIds = linkType === 'similar'
      ? updateProductIds(currentState.similar, productIds, true)
      : currentState.similar;

    linkStateRef.current = {
      ...currentState,
      exact: updatedExactIds,
      similar: updatedSimilarIds,
    };

    mutateMedia(updatedExactIds, updatedSimilarIds, previousState);
  };

  const unlinkProducts = (productIdsToUnlink: string[], linkType: string) => {
    const currentState = linkStateRef.current!;
    const previousState: ProductLinkState = {
      ...currentState,
      exact: [...currentState.exact],
      similar: [...currentState.similar],
    };
    const updatedExactIds = linkType === 'exact'
      ? updateProductIds(currentState.exact, productIdsToUnlink, false)
      : currentState.exact;

    const updatedSimilarIds = linkType === 'similar'
      ? updateProductIds(currentState.similar, productIdsToUnlink, false)
      : currentState.similar;

    linkStateRef.current = {
      ...currentState,
      exact: updatedExactIds,
      similar: updatedSimilarIds,
    };

    mutateMedia(updatedExactIds, updatedSimilarIds, previousState);
  };

  return { linkProducts, unlinkProducts, isLoading };
};

function areProductIdsEqual(first: string[], second: string[]): boolean {
  return first.length === second.length && first.every((id, index) => id === second[index]);
}

function areProductLinkStatesEqual(
  first: ProductLinkState | undefined,
  second: ProductLinkState,
): boolean {
  return first?.mediaId === second.mediaId
    && areProductIdsEqual(first.exact, second.exact)
    && areProductIdsEqual(first.similar, second.similar);
}
